import {
  StudentsDeleteNoteRouteResponse,
  StudentsGetActivityLogRouteResponse,
  StudentsGetAnalyticsRouteResponse,
  StudentsGetInvoicesOutstandingRouteResponse,
  StudentsGetNotesQueryRouteResponse,
  StudentsGetQueryRouteResponse,
  StudentsGetRootRouteResponse,
  StudentsPostInvoiceNewRouteResponse,
  StudentsPostNewRouteBodyInput,
  StudentsPostNewRouteResponse,
  StudentsPostNoteNewRouteResponse,
  StudentsPutActivityLogUpdateRouteBodyInput,
  StudentsPutActivityLogUpdateRouteResponse,
  StudentsPutCourseStateCloseRouteResponse,
  StudentsPutCreditsUpdateRouteResponse,
  StudentsPutNoteUpdateRouteBodyInput,
  StudentsPutNoteUpdateRouteResponse,
  StudentsPutUpdateRouteBodyInput,
  StudentsPutUpdateRouteResponse,
} from '@repo/types/api';

import { cognitoApi } from '../api';
import { EntityResponse, attachId, attachIds } from '../utils';

import {
  ListedStudent,
  ListedStudentActivityLog,
  ListedStudentNote,
  Student,
  StudentAnalytics,
} from './types';

// Define a service using a base URL and expected endpoints
export const studentsApi = cognitoApi.injectEndpoints({
  endpoints: build => ({
    newStudent: build.mutation<
      StudentsPostNewRouteResponse,
      { attributes: StudentsPostNewRouteBodyInput }
    >({
      query(arg) {
        const { attributes } = arg;

        return {
          url: 'students/new',
          body: attributes,
          method: 'POST',
        };
      },
      invalidatesTags: ['Users', 'Students'],
    }),
    getStudent: build.query<EntityResponse<Student>, { studentId: string }>({
      query: arg => `students/${arg?.studentId}`,
      providesTags: (_result, _error, arg) => [{ type: 'Students', id: arg?.studentId }],
      transformResponse(response: StudentsGetRootRouteResponse) {
        return attachId(response, entry => entry.studentId);
      },
    }),
    getStudents: build.query<
      EntityResponse<ListedStudent[]>,
      {
        search?: string;
        excludeDisabled?: boolean;
        gradYear?: number;
        state?: string;
        limit?: number | 'all';
      }
    >({
      query: arg => {
        const { excludeDisabled, gradYear, search, state, limit } = arg;
        return {
          url: 'students/query',
          method: 'GET',
          params: {
            excludeDisabled,
            gradYear,
            search,
            state,
            limit,
          },
        };
      },
      providesTags: result =>
        result
          ? [...result.data.map(({ id }) => ({ type: 'Students' as const, id })), 'Students']
          : ['Students'],
      transformResponse(response: StudentsGetQueryRouteResponse) {
        return attachIds(response, entry => entry.studentId);
      },
    }),
    updateStudent: build.mutation<
      StudentsPutUpdateRouteResponse,
      { studentId: string; newAttributes: StudentsPutUpdateRouteBodyInput }
    >({
      query(arg) {
        const { studentId, newAttributes } = arg;
        return {
          url: `students/${studentId}/update`,
          body: newAttributes,
          method: 'PUT',
        };
      },
      // Invalidates the student but also all Lessons and
      // LessonEnrolments for that student if the student's
      // attendanceType is updated
      invalidatesTags: (_result, _error, arg) =>
        arg.newAttributes.attendanceType
          ? [
              { type: 'Students', id: arg.studentId },
              { type: 'LessonEnrolments', id: arg.studentId },
              'Lessons',
            ]
          : [{ type: 'Students', id: arg.studentId }],
    }),
    getStudentAnalytics: build.query<
      StudentAnalytics,
      { studentId: string; subject: string; topic?: string }
    >({
      query: arg => {
        const { studentId, subject, topic } = arg;
        return {
          url: `students/${studentId}/analytics`,
          method: 'GET',
          params: {
            subject,
            topic,
          },
        };
      },
      providesTags: (_result, _error, arg) => [{ type: 'StudentAnalytics', id: arg?.studentId }],
      transformResponse(response: StudentsGetAnalyticsRouteResponse): StudentAnalytics {
        // The API wraps the payload in `data`; unwrap it. The shape already
        // matches StudentAnalytics (both derived from the shared route schema).
        return response.data;
      },
    }),
    newStudentNote: build.mutation<
      StudentsPostNoteNewRouteResponse,
      {
        studentId: string;
        note: string;
        attributes?: {
          assignee?: string;
          dueDatetime?: string;
          billingRelated?: boolean;
        };
      }
    >({
      query(arg) {
        const { studentId, note, attributes } = arg;

        return {
          url: `students/${studentId}/notes/new`,
          body: {
            note,
            ...attributes,
          },
          method: 'POST',
        };
      },
      invalidatesTags: ['UserNotes'],
    }),
    getStudentNotes: build.query<
      EntityResponse<ListedStudentNote[]>,
      {
        studentId: string;
        active?: boolean;
        billingRelated?: boolean;
        limit?: number | 'all';
      }
    >({
      query(arg) {
        const { studentId, active, billingRelated, limit } = arg;

        return {
          url: `students/${studentId}/notes/query`,
          params: { active, billingRelated, limit },
          method: 'GET',
        };
      },
      providesTags: result =>
        result
          ? [...result.data.map(({ id }) => ({ type: 'UserNotes' as const, id })), 'UserNotes']
          : ['UserNotes'],
      transformResponse(response: StudentsGetNotesQueryRouteResponse) {
        return attachIds(response, entry => `${entry.userId}#${entry.creationDatetime}`);
      },
    }),
    updateStudentNote: build.mutation<
      StudentsPutNoteUpdateRouteResponse,
      {
        studentId: string;
        creationDatetime: number;
        newAttributes: StudentsPutNoteUpdateRouteBodyInput;
      }
    >({
      query(arg) {
        const { studentId, creationDatetime, newAttributes } = arg;

        return {
          url: `students/${studentId}/notes/${creationDatetime}/update`,
          body: newAttributes,
          method: 'PUT',
        };
      },
      // Every user note should be invalidated if the due date of the note is updated, as
      // a note without a due date could gain a due date; in which case it should show
      // up in the follow up page
      invalidatesTags: (_result, _error, arg) =>
        arg.newAttributes.dueDatetime
          ? ['UserNotes']
          : [{ type: 'UserNotes', id: `${arg.studentId}#${arg.creationDatetime}` }],
    }),
    deleteStudentNote: build.mutation<
      StudentsDeleteNoteRouteResponse,
      {
        studentId: string;
        creationDatetime: number;
      }
    >({
      query(arg) {
        const { studentId, creationDatetime } = arg;

        return {
          url: `students/${studentId}/notes/${creationDatetime}`,
          method: 'DELETE',
        };
      },
      invalidatesTags: (_result, _error, arg) => [
        { type: 'UserNotes', id: `${arg.studentId}#${arg.creationDatetime}` },
      ],
    }),
    updateCredits: build.mutation<
      StudentsPutCreditsUpdateRouteResponse,
      {
        studentId: string;
        subject: string;
        course: string;
        stream?: string | undefined;
        credits: {
          termCredits?: number;
          extraCredits?: number;
        };
      }
    >({
      query(arg) {
        const { studentId, subject, course, stream, credits } = arg;

        return {
          url: `students/${studentId}/credits/${subject}/${course}/update`,
          body: {
            stream,
            ...credits,
          },
          method: 'PUT',
        };
      },
      invalidatesTags: (_result, _error, arg) => [{ type: 'Students', id: arg.studentId }],
    }),
    closeCourseState: build.mutation<
      StudentsPutCourseStateCloseRouteResponse,
      {
        studentId: string;
        subject: string;
        course: string;
        stream?: string | undefined;
      }
    >({
      query(arg) {
        const { studentId, subject, course, stream } = arg;

        return {
          url: `students/${studentId}/courseStates/${subject}/${course}/close`,
          body: {
            stream,
          },
          method: 'PUT',
        };
      },
      invalidatesTags: (_result, _error, arg) => [{ type: 'Students', id: arg.studentId }],
    }),
    newInvoices: build.mutation<
      StudentsPostInvoiceNewRouteResponse,
      {
        studentIds: string[];
        auto?: boolean;
      }
    >({
      query(arg) {
        const { studentIds, auto } = arg;

        return {
          url: 'students/invoices/new',
          method: 'POST',
          body: { studentIds, auto },
        };
      },
      invalidatesTags: (_result, _error, arg) => [
        ...arg.studentIds.map(studentId => ({ type: 'Students' as const, id: studentId })),
      ],
    }),
    getStudentActivityLog: build.query<
      EntityResponse<ListedStudentActivityLog[]>,
      {
        teacherId?: string;
        studentId?: string;
        creationDatetimeMin?: number;
      }
    >({
      query(arg) {
        const { teacherId, studentId, creationDatetimeMin } = arg;

        return {
          url: 'students/activityLog',
          params: {
            teacherId,
            studentId,
            creationDatetimeMin,
          },
          method: 'GET',
        };
      },
      providesTags: result =>
        result
          ? [
              ...result.data.map(({ student }) => ({
                type: 'StudentActivityLog' as const,
                id: student,
              })),
              'StudentActivityLog',
            ]
          : ['StudentActivityLog'],
      transformResponse(response: StudentsGetActivityLogRouteResponse) {
        return attachIds(
          response,
          entry => `${entry.student}#${entry.activityType}#${entry.creationDatetime}`
        );
      },
    }),
    updateStudentActivityLog: build.mutation<
      StudentsPutActivityLogUpdateRouteResponse,
      {
        studentId: string;
        activityType: string;
        creationDatetime: number;
        newAttributes: StudentsPutActivityLogUpdateRouteBodyInput;
      }
    >({
      query(arg) {
        const { studentId, activityType, creationDatetime, newAttributes } = arg;

        return {
          url: `students/${studentId}/activityLog/${activityType}/${creationDatetime}/update`,
          body: newAttributes,
          method: 'PUT',
        };
      },
      invalidatesTags: (_result, _error, arg) => [
        { type: 'StudentActivityLog', id: arg.studentId },
      ],
    }),
    getStudentOutstandingInvoices: build.query<
      StudentsGetInvoicesOutstandingRouteResponse,
      { studentId: string }
    >({
      query: arg => `students/${arg.studentId}/invoices/outstanding`,
    }),
  }),
});

export const {
  useNewStudentMutation,
  useLazyGetStudentQuery,
  useGetStudentQuery,
  useGetStudentsQuery,
  useUpdateStudentMutation,
  useGetStudentAnalyticsQuery,
  useDeleteStudentNoteMutation,
  useGetStudentNotesQuery,
  useNewStudentNoteMutation,
  useUpdateStudentNoteMutation,
  useUpdateCreditsMutation,
  useCloseCourseStateMutation,
  useGetStudentActivityLogQuery,
  useUpdateStudentActivityLogMutation,
  useNewInvoicesMutation,
  useGetStudentOutstandingInvoicesQuery,
} = studentsApi;
